// Copyright (c) 2026 SoundDrift. All rights reserved.

import { useState, useEffect, useCallback } from 'react';

const STORAGE_KEY = 'sounddrift_font_scale';
const EVENT_NAME = 'sounddrift_font_scale_change';

function getStoredScale() {
  if (typeof window === 'undefined') return 1.0;
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    const parsed = saved ? parseFloat(saved) : 1.0;
    return isNaN(parsed) ? 1.0 : parsed;
  } catch {
    return 1.0;
  }
}

function applyScaleToDOM(scale) {
  if (typeof document === 'undefined') return;
  const clamped = Math.max(0.8, Math.min(1.8, Number(scale)));
  const sizePx = `${(16 * clamped).toFixed(2)}px`;
  document.documentElement.style.setProperty('--sd-font-scale', String(clamped));
  document.documentElement.style.setProperty('font-size', sizePx, 'important');
  if (document.body) {
    document.body.style.setProperty('--sd-font-scale', String(clamped));
    document.body.style.setProperty('font-size', sizePx);
  }
}

// Immediate application on module load to prevent flash of wrong size
if (typeof window !== 'undefined') {
  applyScaleToDOM(getStoredScale());
}

export function useFontScale() {
  const [scale, setScaleState] = useState(getStoredScale);

  useEffect(() => {
    const handleSync = (newVal) => {
      setScaleState(newVal);
      applyScaleToDOM(newVal);
    };

    const onCustomEvent = (e) => {
      if (e.detail !== undefined) handleSync(Number(e.detail));
    };

    const onStorage = (e) => {
      if (e.key === STORAGE_KEY && e.newValue) {
        handleSync(parseFloat(e.newValue) || 1.0);
      }
    };

    window.addEventListener(EVENT_NAME, onCustomEvent);
    window.addEventListener('storage', onStorage);

    // Ensure DOM is synced on mount
    applyScaleToDOM(scale);

    return () => {
      window.removeEventListener(EVENT_NAME, onCustomEvent);
      window.removeEventListener('storage', onStorage);
    };
  }, [scale]);

  const updateScale = useCallback((newScale) => {
    const clamped = Math.max(0.8, Math.min(1.8, Number(Number(newScale).toFixed(2))));
    setScaleState(clamped);
    applyScaleToDOM(clamped);
    try {
      localStorage.setItem(STORAGE_KEY, String(clamped));
    } catch {}
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent(EVENT_NAME, { detail: clamped }));
    }
  }, []);

  const increase = useCallback(() => {
    updateScale(scale + 0.15);
  }, [scale, updateScale]);

  const decrease = useCallback(() => {
    updateScale(scale - 0.15);
  }, [scale, updateScale]);

  const reset = useCallback(() => {
    updateScale(1.0);
  }, [updateScale]);

  return { scale, setScale: updateScale, increase, decrease, reset };
}
